import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Lock, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ResetPasswordPage — completes the flow started by ForgotPasswordPage.
 * Reads the one-time token from the URL query string, asks for a new
 * password, hits the backend. On success we log the user straight in with
 * the returned token so there's no extra "please sign in" step.
 */
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { hydrateSession } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('This link is missing its reset token. Request a new one.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords don\u2019t match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.detail || 'Could not reset password. Try requesting a new link.');
        setSubmitting(false);
        return;
      }
      // Backend returns a fresh JWT — log the user straight in so they
      // aren't bounced back to /auth to type their new password again.
      if (body.token && body.user) {
        try {
          if (typeof hydrateSession === 'function') {
            hydrateSession(body.token, body.user);
          } else {
            localStorage.setItem('rbr_token', body.token);
          }
        } catch { /* storage unavailable — user can still log in manually */ }
      }
      setDone(true);
      // Small delay so the user sees the success state, then take them home.
      setTimeout(() => navigate('/profile', { replace: true }), 1400);
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen px-5 pt-6 pb-10" style={{ background: '#F7F3EA' }} data-testid="reset-password-page">
      <Link
        to="/auth"
        className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#D6AD60] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to sign in
      </Link>

      <div className="max-w-md mx-auto mt-8">
        <h1
          className="text-3xl mb-2 text-[#173F35]"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
        >
          Set a new password
        </h1>
        <p className="text-sm text-stone-600 mb-8 leading-relaxed">
          Choose something you&rsquo;ll remember. You&rsquo;ll be logged in
          automatically once it&rsquo;s saved.
        </p>

        {done ? (
          <div
            className="rounded-2xl p-5 border"
            style={{ background: '#FFFCF3', borderColor: 'rgba(214,173,96,0.4)' }}
            data-testid="reset-password-success"
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-[#D6AD60]" />
              <h2 className="text-sm font-semibold text-[#173F35]">Password updated</h2>
            </div>
            <p className="text-xs text-stone-600 leading-relaxed">
              Taking you to your profile…
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="reset-password-form">
            <label className="text-xs font-medium text-[#D6AD60] mb-1.5 block">New password</label>
            <div className="relative mb-4">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full pl-11 pr-4 py-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-[#D6AD60] focus:ring-2 focus:ring-[#D6AD60]/20 transition-all"
                data-testid="reset-password-input"
              />
            </div>

            <label className="text-xs font-medium text-[#D6AD60] mb-1.5 block">Confirm new password</label>
            <div className="relative mb-4">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Type it again"
                className="w-full pl-11 pr-4 py-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-[#D6AD60] focus:ring-2 focus:ring-[#D6AD60]/20 transition-all"
                data-testid="reset-password-confirm-input"
              />
            </div>

            {error && (
              <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-2xl" data-testid="reset-password-error">
                <p className="text-xs text-red-500">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !token}
              className="w-full py-3.5 btn-gold rounded-2xl text-sm font-semibold active:scale-[0.98] transition-all duration-300 disabled:opacity-60"
              data-testid="reset-password-submit-btn"
            >
              {submitting ? 'Saving…' : 'Save new password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
